import {useCallback} from 'react';
import {v4 as uuid} from 'uuid';
import {contentLabActions, ErrorKind} from '@contentlab/stores/contentLabStore';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {CONTENT_STUDIO_PRICING_SOURCES} from '@/lib/analytics/analyticsConstants';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import Duration from '@/types/models/pricing/duration';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import {WEB_PAYMENTS_URL} from '@/views/content-studio/pricing/constants';
import {saveSessionIdsToStorage} from '@/views/content-studio/pricing/utils';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useNavigation from '@/hooks/use-navigation';
import {contentStudioPricingModalActions} from '@/stores/contentStudioPricingModalStore';

type UseHandlePricingCtaClickProps = {
  shouldSkipUpgradeModal: boolean;
  sendUiInteractionEvent: (buttonName: string) => void;
  sendPurchaseProcessStartedEvent: (
    product_id: string,
    process_id: string
  ) => void;
  enforcePaymentManagerRole?: boolean;
};

function useHandlePricingCtaClick({
  shouldSkipUpgradeModal,
  sendUiInteractionEvent,
  sendPurchaseProcessStartedEvent,
  enforcePaymentManagerRole = false,
}: UseHandlePricingCtaClickProps) {
  const navigation = useNavigation();
  const {dashboardUser} = useDashboardUserQuery({});

  const handleCtaClick = useCallback(
    (
      activeSubscriptionId: string | undefined,
      activePlan: SubscriptionPlan | undefined,
      selectedPlan: SubscriptionPlan,
      buttonName: string,
      isTrialFeatureEnabled: boolean
    ) => {
      sendUiInteractionEvent(buttonName);

      if (enforcePaymentManagerRole && !dashboardUser.hasPaymentManagerRole) {
        const paymentManagerEmail =
          dashboardUser?.agency?.organizationPlatformPaymentManager?.email;

        contentLabActions.addError({
          title: 'Upgrade unavailable',
          description: paymentManagerEmail
            ? `Contact your organization's payment manager: ${paymentManagerEmail}.`
            : "Contact your organization's payment manager.",
          kind: ErrorKind.FAILURE,
        });
        return;
      }

      if (activePlan && activePlan.bundle !== ContentLabPlanBundle.FREE) {
        contentStudioPricingModalActions.openConfirmModal({
          source: shouldSkipUpgradeModal
            ? CONTENT_STUDIO_PRICING_SOURCES.PRICING_PAGE
            : CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
          plans: {
            currentSubscriptionId: activeSubscriptionId || '',
            currentPlan: activePlan,
            selectedPlan,
          },
          navigationContext: shouldSkipUpgradeModal
            ? 'direct_to_confirm'
            : 'via_upgrade',
        });
      } else {
        const planDuration =
          selectedPlan.duration === Duration.MONTHLY ? 'monthly' : 'yearly';
        let bundle = 'starter';
        if (selectedPlan.bundle === ContentLabPlanBundle.STUDIO) {
          bundle = 'studio';
        } else if (selectedPlan.bundle === ContentLabPlanBundle.PRO) {
          bundle = 'pro';
        } else if (selectedPlan.bundle === ContentLabPlanBundle.BUSINESS) {
          bundle = 'business';
        } else if (selectedPlan.bundle === ContentLabPlanBundle.STARTER) {
          bundle = 'starter';
        }
        const processId = uuid();
        sendPurchaseProcessStartedEvent(selectedPlan.id, processId);
        AnalyticsService.dischargeEventBatch();

        saveSessionIdsToStorage(selectedPlan.id, processId);
        const {trialQuoteId, id} = selectedPlan;
        const quoteId = isTrialFeatureEnabled ? (trialQuoteId ?? id) : id;

        const pathSegments = [bundle];
        if (bundle !== 'starter') {
          pathSegments.push(planDuration);
        }
        const planPath = pathSegments.join('-');

        const urlPath = `${WEB_PAYMENTS_URL}/${planPath}/payment?quoteId=${quoteId}`;

        navigation.navigate(urlPath, null, false);
      }
    },
    [
      navigation,
      sendUiInteractionEvent,
      sendPurchaseProcessStartedEvent,
      enforcePaymentManagerRole,
      dashboardUser.hasPaymentManagerRole,
      dashboardUser?.agency?.organizationPlatformPaymentManager?.email,
    ]
  );

  return {handleCtaClick};
}

export default useHandlePricingCtaClick;
